import React, { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import followService from "../../services/followService";
import communityService from "../../services/communityService";
import { useRecipesTranslation } from "../common/RecipeTranslator";
import UserCard from "./UserCard";
import RecipeCard from "./RecipeCard";

/**
 * Onglet des abonnements
 * Affiche les utilisateurs suivis et leur fil d'actualité
 */
const FollowingTab = ({ user, token }) => {
  const { t } = useLanguage();
  const [following, setFollowing] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("feed"); // feed ou following
  const { translatedRecipes: translatedFeed, isTranslating } =
    useRecipesTranslation(feed);

  useEffect(() => {
    if (user) {
      loadFollowingData();
    }
  }, [user]);

  const loadFollowingData = async () => {
    setLoading(true);
    try {
      const [followingData, feedData] = await Promise.all([
        followService.getFollowing(user.id, 50),
        communityService.getFeed(20, 0),
      ]);

      setFollowing(followingData.following || []);
      setFeed(feedData.feed || []);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center py-10 sm:py-20 px-3">
        <div className="bg-white dark:bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 p-6 sm:p-12 max-w-md mx-auto">
          <div className="text-5xl sm:text-6xl mb-3 sm:mb-4" aria-hidden>
            🔐
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white mb-2">
            {t("loginRequired")}
          </h3>
          <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base">{t("loginToSeeFollowing")}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-ink-700 dark:text-white">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Sélecteur de vue - stack ou row selon écran */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-ink-100 dark:bg-white/10 backdrop-blur-sm rounded-xl p-1.5 sm:p-1 border border-ink-900/10 dark:border-white/20">
        <button
          onClick={() => setActiveView("feed")}
          className={`flex-1 min-w-0 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-all duration-200 touch-manipulation ${
            activeView === "feed"
              ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md"
              : "text-ink-700 hover:bg-ink-100 dark:text-white dark:hover:bg-white/20"
          }`}
        >
          <span className="truncate block">📰 {t("newsFeed")} ({feed.length})</span>
        </button>
        <button
          onClick={() => setActiveView("following")}
          className={`flex-1 min-w-0 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-all duration-200 touch-manipulation ${
            activeView === "following"
              ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md"
              : "text-ink-700 hover:bg-ink-100 dark:text-white dark:hover:bg-white/20"
          }`}
        >
          <span className="truncate block">👥 {t("following")} ({following.length})</span>
        </button>
      </div>

      {/* Contenu */}
      {activeView === "feed" ? (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex flex-wrap items-center gap-2 mb-4 sm:mb-6">
            <span aria-hidden>📰</span>
            <span>{t("yourNewsFeed")}</span>
            {isTranslating && (
              <span className="text-xs sm:text-sm text-orange-300 animate-pulse flex items-center gap-1">
                <span className="animate-spin">🌐</span>
                {t("translating")}
              </span>
            )}
          </h2>

          {feed.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {(translatedFeed || feed).map((recipe) => (
                <RecipeCard key={recipe.id} recipe={recipe} user={user} />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 sm:py-16 bg-white dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 px-4">
              <div className="text-5xl sm:text-6xl mb-3 sm:mb-4" aria-hidden>
                📭
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-ink-900 dark:text-white mb-2">
                {t("noNews")}
              </h3>
              <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base mb-4 sm:mb-6">
                {t("followCooksToSeeRecipes")}
              </p>
              <button
                onClick={() => setActiveView("following")}
                className="px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium text-sm sm:text-base hover:from-orange-600 hover:to-amber-600 shadow-md transition-all duration-200 touch-manipulation"
              >
                {t("findCooks")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex items-center gap-2 mb-4 sm:mb-6">
            <span aria-hidden>👥</span>
            <span>{t("yourFollowing")}</span>
          </h2>

          {following.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {following.map((followedUser) => (
                <UserCard
                  key={followedUser.id}
                  user={followedUser}
                  currentUser={user}
                  onFollowChange={loadFollowingData}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 sm:py-16 bg-white dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 px-4">
              <div className="text-5xl sm:text-6xl mb-3 sm:mb-4" aria-hidden>
                👥
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-ink-900 dark:text-white mb-2">
                {t("notFollowingAnyone")}
              </h3>
              <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base mb-4 sm:mb-6">
                {t("startFollowingToSeeRecipes")}
              </p>
              <button
                onClick={() => (window.location.href = "#explorer")}
                className="px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium text-sm sm:text-base hover:from-orange-600 hover:to-amber-600 shadow-md transition-all duration-200 touch-manipulation"
              >
                {t("exploreCommunity")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FollowingTab;
